import {Alert} from 'src/design/atoms/Alert';
import {Divider} from 'src/design/atoms/Divider';
import {Grid} from 'src/design/atoms/Grid';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Switch} from 'src/design/atoms/Switch';
import {Text} from 'src/design/atoms/Text';
import {IMAGE_MIME_TYPE} from '@mantine/dropzone';
import {useDisclosure} from '@mantine/hooks';
import {IconShieldLock} from '@tabler/icons-react';
import React, {useEffect, useMemo, useRef} from 'react';
import {FeatureFlagEnum} from 'src/api/ProactApiClient';
import {ProactField} from 'src/app/forms/engine/ProactField';
import {ProactForm} from 'src/app/forms/engine/ProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import {Description} from 'src/design/atoms/Description';
import {SectionTitle} from 'src/design/atoms/Typography';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {useGetFormCategorySelectOptionsQuery} from 'src/redux/queries/formCategory';
import {useGetUserGroupSelectOptionsQuery} from 'src/redux/queries/userGroup';
import {useSectionValidation} from 'src/shared/hooks/SectionValidationContext';
import useActiveFeatureFlags from 'src/shared/hooks/useActiveFeatureFlags';
import type {ISelectOption} from 'src/types/Form';
import type {IFormDataV2, IFormFieldV2, IFormSettings} from 'src/types/FormV2';
import {
  useGetFormSettings,
  useGetSettingsSeed,
  useLoadFormSettings,
  useUpdateFormSettings,
} from '../hooks/useFormBuilder';

// Define form fields using IFormFieldV2 interface
const formFields: IFormFieldV2[] = [
  {
    name: 'projectId',
    label: 'Project',
    type: 'project',
    required: false,
    description:
      'Assign this form to a specific project, or leave as instance-wide to make it available to all projects.',
  },
  {
    name: 'categoryId',
    label: 'Category',
    type: 'select',
    required: false,
    description:
      'Groups this form under a category on the My Forms tab. Leave empty to list it under Uncategorized.',
  },
  {
    name: 'identifier',
    label: 'Unique Form Identifier',
    type: 'text',
    required: true,
    description:
      'A unique, 7 character, identifier for this form. Used to generate a unique identifier for each form submission of this form. Cannot be edited once the form is created. Ex., FRMHELP',
    validation: {
      minLength: 7,
      maxLength: 7,
    },
    textboxOptions: {
      validationType: 'safe',
    },
  },
  {
    name: 'name',
    label: 'Form Name',
    type: 'text',
    required: true,
    size: 'md',
    description: 'The name of the form in the system.',
    validation: {
      maxLength: 50,
    },
  },
  {
    name: 'title',
    label: 'Title',
    type: 'text',
    required: false,
    size: 'md',
    description: 'The title shown on the form when being completed by a user.',
    validation: {
      maxLength: 50,
    },
  },
  {
    name: 'description',
    label: 'Description',
    type: 'textarea',
    required: false,
    description:
      'The description shown on the form when being completed by the user.',
    validation: {
      maxLength: 500,
    },
    textboxOptions: {
      multiline: true,
    },
  },
  {
    name: 'active',
    label: 'Form Is Active',
    type: 'switch',
    required: true,
    description:
      'If enabled, the form is accessible throughout the system and users may create submissions for it.',
    switchOptions: {control: 'toggle'},
  },
  {
    name: 'anonymousSubmission',
    label: 'Enable Anonymous Submission',
    type: 'switch',
    required: true,
    description:
      'If enabled, this form will not collect user information during form submission.',
    switchOptions: {control: 'toggle'},
  },
  {
    name: 'subformOnly',
    label: 'Enable Embedded Subform Only',
    type: 'switch',
    required: true,
    description:
      'If enabled, this form may only be used as a sub-form of another form. It will have limited accessibility throughout the system.',
    switchOptions: {control: 'toggle'},
  },
  {
    name: 'extensionOnly',
    label: 'Extension Tab Only',
    type: 'switch',
    required: true,
    description:
      'If enabled, this form is only accessible through module extension tabs. It will be hidden from user-facing form lists and submissions.',
    switchOptions: {control: 'toggle'},
  },
  {
    name: 'enableFormIdentifierField',
    label: 'Enable Form Description Field',
    type: 'switch',
    required: true,
    description:
      'If enabled, this form will require the user to complete the form description field when submitting the form. This information is used for identification purposes in other parts of this website. If disabled, the field will not be shown.',
    switchOptions: {control: 'toggle'},
  },
  {
    name: 'triggerAnalyticSafetyEvent',
    label: 'Enable Analytics Safety Events',
    type: 'switch',
    required: true,
    description:
      'If enabled, this form will trigger a Safety Event upon form submission.',
    switchOptions: {control: 'toggle'},
  },
  {
    name: 'failable',
    label: 'Form Is Failable',
    type: 'switch',
    required: true,
    description:
      'If enabled, the form can be failed by the user. A checkbox or switch field must be configured to allow the user to fail the form.',
    switchOptions: {control: 'toggle'},
  },
  {
    name: 'accessAllowUserGroups',
    label: 'Access by Groups',
    type: 'select-multiple',
    required: false,
    description:
      'If selected, the selected groups will have access to all form submissions for this form.',
  },
  {
    name: 'accessAllowSupervisors',
    label: "Access for User's Supervisor",
    type: 'switch',
    required: true,
    description:
      'If enabled, the supervisor of the user who submits this form will be able to access the submissions of any of their direct reports.',
    switchOptions: {control: 'toggle'},
  },
  {
    name: 'accessAllowEditAfterPublish',
    label: 'Allow Edits After Publish',
    type: 'switch',
    required: true,
    description:
      'If enabled, the original user that submitted the form and any other user that has access to the form submission may edit the form submission. Editing form submissions does not re-trigger workflows or compliance events. Editing form submissions may re-trigger notification rules.',
    switchOptions: {control: 'toggle'},
  },
  {
    name: 'referenceImage',
    label: 'Reference Image',
    type: 'file',
    required: false,
    description:
      'If an image is provided, it will be shown to the user when submitting a form.',
    fileUploadOptions: {
      allowMultiple: false,
      allowedFileTypes: IMAGE_MIME_TYPE,
      isHideUploadSuccessEnabled: true,
    },
  },
];

export interface FormSettingsProps {
  reveal?: () => void;
}
export const FormSettings: React.FC<FormSettingsProps> = ({reveal}) => {
  const activeFeatureFlags = useActiveFeatureFlags();
  const updateFormSettings = useUpdateFormSettings();
  const loadFormSettings = useLoadFormSettings();
  const formSettings = useGetFormSettings();
  const settingsSeed = useGetSettingsSeed();
  const {registerSection} = useSectionValidation();

  const [
    sensitiveConfirmOpened,
    {open: openSensitiveConfirm, close: closeSensitiveConfirm},
  ] = useDisclosure(false);

  const {
    data: usergroups,
    isFetching: isUserGroupsFetching,
    isLoading: isUserGroupsLoading,
  } = useGetUserGroupSelectOptionsQuery({
    active: true,
    start: 0,
    length: 10000,
  });

  const {data: categories, isLoading: isCategoriesLoading} =
    useGetFormCategorySelectOptionsQuery();

  // Populate form data from settings
  const formData = useMemo<IFormDataV2>(() => {
    return {
      projectId: formSettings.projectId ?? null,
      categoryId: formSettings.categoryId ?? '',
      identifier: formSettings.identifier || '',
      name: formSettings.name || '',
      title: formSettings.title || '',
      description: formSettings.metadata.description || '',
      active: formSettings.active,
      anonymousSubmission: formSettings.metadata.anonymousSubmission,
      subformOnly: formSettings.subformOnly,
      extensionOnly: formSettings.extensionOnly,
      enableFormIdentifierField:
        formSettings.metadata.enableFormIdentifierField,
      triggerAnalyticSafetyEvent: formSettings.triggerAnalyticSafetyEvent,
      failable: formSettings.failable,
      accessAllowUserGroups: formSettings.accessAllowUserGroups,
      accessAllowSupervisors: formSettings.accessAllowSupervisors,
      accessAllowEditAfterPublish: formSettings.accessAllowEditAfterPublish,
      referenceImage: formSettings?.metadata?.cache?.referenceImage || '[]',
    };
  }, [formSettings]);

  // For CheckboxList component which uses IListItem
  const userGroupOptions = useMemo(
    () =>
      usergroups?.data?.map(
        i =>
          ({
            id: String(i.id),
            label: i.label,
          } as ISelectOption)
      ) ?? [],
    [usergroups]
  );

  const categoryOptions = useMemo(
    () =>
      categories?.map(
        category =>
          ({
            id: String(category.id),
            label: category.label ?? '',
          } as ISelectOption)
      ) ?? [],
    [categories]
  );

  // Update form meta with dynamic options
  const formMeta = useMemo(() => {
    return formFields.map(field => {
      // Hide safety events switch if feature flag is not active
      if (
        field.name === 'triggerAnalyticSafetyEvent' &&
        !activeFeatureFlags.includes(FeatureFlagEnum.SafetyEvents)
      ) {
        return {
          ...field,
          visibility: {isDefaultHidden: true},
        };
      }
      if (field.name === 'categoryId') {
        return {
          ...field,
          options: categoryOptions,
          disabled: isCategoriesLoading,
        };
      }
      // Only disable identifier field in edit mode, not in create mode
      if (field.name === 'identifier' && formSettings.lockIdentifier) {
        return {
          ...field,
          disabled: true,
        };
      }
      // Disable supervisor access when anonymous submission or sensitive data is enabled
      if (field.name === 'accessAllowSupervisors') {
        if (formSettings.sensitiveData) {
          return {
            ...field,
            disabled: true,
            description:
              'Supervisor access is not available for sensitive forms',
          };
        }
        if (formSettings.metadata?.anonymousSubmission) {
          return {
            ...field,
            disabled: true,
            description:
              "Supervisor access is not available for anonymous forms because the submitter's identity is not recorded.",
          };
        }
      }
      // Disable anonymous submission when sensitive data is enabled
      if (field.name === 'anonymousSubmission' && formSettings.sensitiveData) {
        return {
          ...field,
          disabled: true,
          description:
            'Anonymous submission is not available for sensitive forms',
        };
      }
      // Disable subformOnly when sensitive data is enabled
      if (field.name === 'subformOnly' && formSettings.sensitiveData) {
        return {
          ...field,
          disabled: true,
          description: 'Sensitive forms cannot be used as subforms',
        };
      }
      // Add options to accessAllowUserGroups field
      if (field.name === 'accessAllowUserGroups') {
        if (formSettings.sensitiveData) {
          return {
            ...field,
            options: userGroupOptions,
            disabled: true,
            description:
              'Form access groups are not available for sensitive forms',
          };
        }
        return {
          ...field,
          options: userGroupOptions,
          disabled: isUserGroupsFetching || isUserGroupsLoading,
        };
      }
      return field;
    });
  }, [
    activeFeatureFlags,
    formSettings.lockIdentifier,
    formSettings.sensitiveData,
    formSettings.metadata?.anonymousSubmission,
    userGroupOptions,
    isUserGroupsFetching,
    isUserGroupsLoading,
    categoryOptions,
    isCategoriesLoading,
  ]);

  const form = useProactForm({formMeta, initialData: formData});
  const {methods, resetForm, validateForm} = form;

  useEffect(() => {
    return registerSection({
      id: 'form-settings',
      label: 'Form Settings',
      validateForm,
      reveal,
    });
  }, [registerSection, validateForm, reveal]);

  // settingsSeed only increments when formSettings is replaced from a
  // truth-source (async load, sensitive-data toggle) via loadFormSettings —
  // never on the per-keystroke updateFormSettings mirror below, so this
  // does not fight typing.
  const seededSettingsRef = useRef(settingsSeed);
  useEffect(() => {
    if (settingsSeed === seededSettingsRef.current) {
      return;
    }
    seededSettingsRef.current = settingsSeed;
    resetForm(formData);
  }, [settingsSeed, formData, resetForm]);

  // Mirrors user-driven edits into the form builder's settings slice. Reads
  // the latest settings/updater through refs so the subscription does not
  // need to be torn down and recreated on every settings change.
  const formSettingsRef = useRef(formSettings);
  formSettingsRef.current = formSettings;
  const updateFormSettingsRef = useRef(updateFormSettings);
  useEffect(() => {
    updateFormSettingsRef.current = updateFormSettings;
  });

  useEffect(() => {
    const subscription = methods.watch((values, {name}) => {
      if (!name) return;

      const value = values[name as keyof IFormDataV2];
      const currentSettings = formSettingsRef.current;

      if (name === 'description') {
        updateFormSettingsRef.current({
          ...currentSettings,
          metadata: {
            ...currentSettings.metadata,
            description: value as string | undefined,
          },
        });
      } else if (
        name === 'anonymousSubmission' ||
        name === 'enableFormIdentifierField'
      ) {
        updateFormSettingsRef.current({
          ...currentSettings,
          metadata: {
            ...currentSettings.metadata,
            [name]: value,
          },
        });
      } else if (name === 'accessAllowUserGroups') {
        updateFormSettingsRef.current({
          ...currentSettings,
          accessAllowUserGroups:
            (value as ISelectOption[] | undefined)?.map(i => +i.id) ?? [],
        });
      } else if (name === 'categoryId') {
        updateFormSettingsRef.current({
          ...currentSettings,
          categoryId: value ? Number(value) : null,
        });
      } else if (name === 'referenceImage') {
        updateFormSettingsRef.current({
          ...currentSettings,
          metadata: {
            ...currentSettings.metadata,
            cache: {
              ...currentSettings.metadata?.cache,
              referenceImage: value ?? [],
            },
          },
        });
      } else {
        updateFormSettingsRef.current({
          ...currentSettings,
          [name]: value,
        } as IFormSettings);
      }
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  const handleSensitiveDataToggle = (checked: boolean) => {
    if (checked && formSettings.metadata.anonymousSubmission) {
      // Show confirmation dialog before enabling
      openSensitiveConfirm();
    } else {
      applyEnableSensitiveData(checked);
    }
  };

  const applyEnableSensitiveData = (enabled: boolean) => {
    if (enabled) {
      // Enabling sensitive data: also disable anonymous submission, subformOnly, and clear access groups
      loadFormSettings({
        ...formSettings,
        sensitiveData: true,
        subformOnly: false,
        accessAllowUserGroups: [],
        metadata: {
          ...formSettings.metadata,
          anonymousSubmission: false,
        },
      });
    } else {
      loadFormSettings({
        ...formSettings,
        sensitiveData: false,
      });
    }
  };

  const handleSensitiveConfirm = () => {
    closeSensitiveConfirm();
    applyEnableSensitiveData(true);
  };

  return (
    <>
      <ProactForm form={form}>
        <SimpleGrid cols={{base: 1, lg: 2}}>
          <Grid gutter="xs">
            <ProactField name="projectId" />
            <ProactField name="categoryId" />
            <ProactField name="identifier" />
            <ProactField name="name" />
            <ProactField name="title" />
            <ProactField name="description" />
          </Grid>

          <Grid gutter="xs">
            <Grid.Col span={12}>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                Behavior
              </Text>
            </Grid.Col>
            <ProactField name="active" />
            <ProactField name="failable" />
            <ProactField name="accessAllowEditAfterPublish" />
            <ProactField name="anonymousSubmission" />
            <ProactField name="subformOnly" />
            <ProactField name="enableFormIdentifierField" />

            <Grid.Col span={12}>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt="sm">
                Integrations
              </Text>
            </Grid.Col>
            <ProactField name="extensionOnly" />
            <ProactField name="triggerAnalyticSafetyEvent" />
          </Grid>
        </SimpleGrid>

        <Divider />
        <SectionTitle text="Form Submission Access" />
        <Description text="Manage access options for submitted forms. The user who submits a form can always view their own form submissions." />
        <SimpleGrid cols={{base: 1, lg: 2}} spacing="lg" mt="lg">
          <Grid gutter="xs">
            <ProactField name="accessAllowUserGroups" />
            <ProactField name="accessAllowSupervisors" />
          </Grid>
          <Alert
            color="yellow"
            variant="light"
            title="Sensitive Data"
            icon={<IconShieldLock size={20} />}>
            <Switch
              label="Enable Sensitive Data Mode"
              checked={formSettings.sensitiveData}
              onChange={e => handleSensitiveDataToggle(e.currentTarget.checked)}
              mt="xs"
            />
            <Text size="xs" c="dimmed" mt="sm">
              When enabled, form submissions are restricted to the submission
              creator only. No other user — including access group members,
              supervisors, or users with the Form Submissions permission — can
              view another user's submissions.
            </Text>
            {formSettings.sensitiveData && (
              <Text size="xs" c="dimmed" mt="xs">
                Supervisor access, User Group access, Anonymous submission and
                subform mode are disabled while sensitive data is active.
              </Text>
            )}
          </Alert>
        </SimpleGrid>

        <Divider />
        <Grid gutter="xs">
          <ProactField name="referenceImage" />
        </Grid>
      </ProactForm>

      <ConfirmDialog
        opened={sensitiveConfirmOpened}
        onClose={closeSensitiveConfirm}
        onConfirm={handleSensitiveConfirm}
        title="Enable Sensitive Data?"
        body="This form currently allows anonymous submissions. Enabling sensitive data will disable anonymous submissions. Existing anonymous submissions will retain their anonymity but will only be accessible by the submission creator."
        buttonTextYes="Enable"
        buttonTextNo="Cancel"
        confirmationButtonColor="yellow"
      />
    </>
  );
};
